import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import FocusIcon from "@mui/icons-material/CenterFocusWeak";
import { useTranslate } from "ra-core";
import { ToolGroupManager, CrosshairsTool } from "@cornerstonejs/tools";
import { segmentColorsToCSS } from "shared/utils/color";
import { Marker } from "./Marker";
import { useSnapshot } from "valtio";
import viewerState from "stores/viewer";
import getIds from "lib/cornerstone/helpers/getIds";
import { cache, getRenderingEngine } from "@cornerstonejs/core";
import { Point3 } from "@cornerstonejs/core/dist/esm/types";
interface EyeProps {
  marker: Marker;
  displayMarker: (
    { segments, seriesUID }: Pick<Marker, "seriesUID" | "segments">,
    onlySelect?: boolean,
  ) => void;
}

function Eye({ marker, displayMarker }: EyeProps) {
  const { segments, seriesUID } = marker;
  const t = useTranslate();

  const { segmentsPerSeries, instanceMetadata, focusedMarkerCode } =
    useSnapshot(viewerState);

  if (!segments?.filter((s) => !!s).length) return <span className="mx-4" />;
  const segmentsState = segmentsPerSeries.filter((seg) =>
    segments.some(
      (s) =>
        s.SegmentNumber === seg.SegmentNumber &&
        s.seriesUID === seg.seriesUID &&
        s.representationUID === seg.representationUID,
    ),
  );

  const isSelected = segmentsState.every((seg) => seg?.selected);
  const disabled = segmentsPerSeries.every((s) => !s.representationUID);

  const otherModality = !segments.some(
    (s) => s.seriesUID === instanceMetadata?.SeriesInstanceUID,
  );
  if (marker.coord) {
    const isLoading = !segmentsState.every((s) => s.representationUID);
    const isFocused = focusedMarkerCode === marker.code;
    const focusColor =
      segmentColorsToCSS(segments) ||
      ((segments?.[0] as any)?._rgbColor
        ? { color: `rgb(${(segments[0] as any)._rgbColor.join(",")})` }
        : null);

    return (
      <IconButton
        size="small"
        disabled={disabled}
        title={t("patient.view.tooltip.marker.focus")}
        aria-label={t("patient.view.tooltip.marker.focus")}
        onClick={() => {
          viewerState.focusedMarkerCode = marker.code;
          displayMarker({ segments, seriesUID }, true);
          const delay = otherModality ? 700 : 0;
          setTimeout(() => moveCameraTo(marker.coord), delay);
        }}
      >
        <FocusIcon
          className={isLoading ? "opacity-50" : ""}
          style={isFocused ? focusColor : null}
        />
      </IconButton>
    );
  }

  if (isSelected) {
    const isLoading = !segmentsState.every((s) => s.representationUID);
    return (
      <IconButton
        size="small"
        title={t("patient.view.tooltip.marker.hide")}
        aria-label={t("patient.view.tooltip.marker.hide")}
        onClick={() => displayMarker({ segments, seriesUID })}
      >
        <VisibilityIcon
          className={isLoading ? "opacity-5" : ""}
          style={
            segmentColorsToCSS(segments) ||
            ((segments?.[0] as any)?._rgbColor
              ? { color: `rgb(${(segments[0] as any)._rgbColor.join(",")})` }
              : null)
          }
        />
      </IconButton>
    );
  }

  return (
    <IconButton
      disabled={disabled}
      size="small"
      title={t("patient.view.tooltip.marker.show")}
      aria-label={t("patient.view.tooltip.marker.show")}
      onClick={() => displayMarker({ segments, seriesUID })}
    >
      <VisibilityOffIcon
        className={
          otherModality
            ? "opacity-50"
            : disabled
            ? "animate-pulse-fast"
            : "" + disabled
        }
      />
    </IconButton>
  );
}
function moveCameraTo(position: Point3) {
  const { renderingEngineId, volumeId, toolGroupId } = getIds();
  const renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) return;

  let volume: any;
  const meta = viewerState.instanceMetadata as any;
  if (meta?._niftiUrl) {
    const activeMod = viewerState.niftiActiveModality ?? meta._activeModality;
    const activeBaseUrl =
      (viewerState.niftiBases as any)?.[activeMod] ?? meta._niftiUrl;
    volume = cache.getVolume(`nifti:${activeBaseUrl}`);
  }
  if (!volume) {
    volume = cache.getVolume(volumeId);
  }
  if (!volume) return;

  // Convert voxel coordinates to world coordinates
  const [x, y, z] = position;
  const worldPos = volume.imageData.indexToWorld([x, y, z]) as Point3;

  // Use crosshairs tool's setToolCenter — moves cameras + crosshair lines
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (toolGroup) {
    const crosshairsTool = toolGroup.getToolInstance(
      CrosshairsTool.toolName,
    ) as any;
    if (crosshairsTool?.setToolCenter) {
      crosshairsTool.setToolCenter(worldPos);
      return;
    }
  }
}

export default Eye;
